// src/main/TenantLedger.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Card,
    CardContent,
    Avatar,
    Tabs,
    Tab,
    Alert,
    Snackbar,
    FormControlLabel,
    Switch,
    CircularProgress,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    Badge,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Rating,
    Fab,
    Zoom,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    CheckCircle,
    Cancel,
    People,
    AttachMoney,
    Email as EmailIcon,
    Send,
    Download,
    Receipt as ReceiptIcon,
    ReceiptLong,
    Pending,
    Error as ErrorIcon,
    Verified,
    Schedule,
    CloudDownload,
    LocalPrintshop,
    QrCodeScanner,
    History,
    CheckCircleOutline,
    Warning,
    Info,
    Phone,
    Person,
    Business,
    Home,
    CalendarToday,
    Description,
    Note,
    AttachFile,
    CloudUpload,
    AccountBalance,
    TrendingUp,
    TrendingDown,
    Calculate,
    Adjust,
    LocalAtm,
    MoneyOff,
    VerifiedUser,
    Balance,
    Receipt,
    EventNote,
    AssignmentInd,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Summarize,
    Build
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APITenantList,
    APIAssignmentList,
    APITenantLedgerList,
    APITenantLedgerEntryStore,
    APITenantLedgerEntryUpdate,
    APITenantLedgerEntryDelete,
    APITenantLedgerExportExcel,
    APITenantLedgerExportPDF,
    APITenantLedgerPrint,
    APITenantLedgerStats,
    APITenantLedgerRecalculate,
    APITenantLedgerVerify,
    APITenantLedgerBalance
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const TenantLedger = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        ledgerEntries: [],
        tenantBalances: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [statistics, setStatistics] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [tenantBalance, setTenantBalance] = useState(null);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false);
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    // Entry type options
    const entryTypeOptions = [
        { id: 'opening_balance', name: 'Opening Balance', icon: <AccountBalance />, color: 'primary', sign: 'debit' },
        { id: 'monthly_rent', name: 'Monthly Rent', icon: <Home />, color: 'warning', sign: 'debit' },
        { id: 'payment', name: 'Payment', icon: <AttachMoney />, color: 'success', sign: 'credit' },
        { id: 'late_fee', name: 'Late Fee', icon: <Warning />, color: 'error', sign: 'debit' },
        { id: 'adjustment', name: 'Adjustment', icon: <Adjust />, color: 'info', sign: 'both' },
        { id: 'maintenance_deduction', name: 'Maintenance Deduction', icon: <Build />, color: 'error', sign: 'debit' }
    ];

    // Form data for Ledger Entry
    const [formData, setFormData] = useState({
        tenant_id: '',
        entry_date: new Date().toISOString().split('T')[0],
        entry_type: 'monthly_rent',
        debit: '',
        credit: '',
        notes: '',
        entry_verified: false
    });

    const [filters, setFilters] = useState({
        tenant_id: '',
        entry_type: '',
        entry_verified: '',
        date_from: '',
        date_to: '',
        min_amount: '',
        max_amount: ''
    });

    const [errors, setErrors] = useState({
        tenant_id: false,
        entry_date: false,
        entry_type: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchTenants();
        fetchLedgerEntries();
        fetchStatistics();
        fetchTenantBalances();
    }, [showToast]);

    // Fetch tenants
    const fetchTenants = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APITenantList);
            if (result.success) {
                setTenants(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching tenants:", error);
            setTenants([]);
        }
    };

    // Fetch ledger entries
    const fetchLedgerEntries = async (tenantId = null) => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const params = tenantId ? { tenant_id: tenantId } : {};
            const result = await apiService.list(userBranchId, APITenantLedgerList, params);
            if (result.success) {
                const entries = Array.isArray(result.data) ? result.data : [];
                setLedgerEntries(entries);
                setPageState(prev => ({
                    ...prev,
                    ledgerEntries: entries
                }));
                if (tenantId && entries.length > 0) {
                    setTenantBalance(entries[entries.length - 1]?.running_balance || 0);
                }
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch tenant balances summary
    const fetchTenantBalances = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APITenantLedgerBalance);
            if (result.success) {
                setPageState(prev => ({
                    ...prev,
                    tenantBalances: Array.isArray(result.data) ? result.data : []
                }));
            }
        } catch (error) {
            console.error("Error fetching balances:", error);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APITenantLedgerStats);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Recalculate ledger
    const handleRecalculate = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to recalculate ledger", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedTenant) {
                formDataToSend.append('tenant_id', selectedTenant);
            }

            const result = await apiService.create(APITenantLedgerRecalculate, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                setRecalculateDialogOpen(false);
                fetchLedgerEntries(selectedTenant);
                fetchTenantBalances();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Recalculate error:", error);
            showToast("Error", "Failed to recalculate ledger", "error");
        }
    };

    // Verify entry
    const handleVerifyEntry = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to verify entries", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('entry_id', selectedEntry.id);

            const result = await apiService.create(APITenantLedgerVerify, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                setVerifyDialogOpen(false);
                fetchLedgerEntries(selectedTenant);
                fetchStatistics();
            }
        } catch (error) {
            console.error("Verify error:", error);
            showToast("Error", "Failed to verify entry", "error");
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrintLedger = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print ledger", "error");
            return;
        }
        
        if (!selectedTenant) {
            showToast("Validation Error", "Please select a tenant first", "warning");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('tenant_id', selectedTenant);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));

            const result = await apiService.print(APITenantLedgerPrint, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Ledger printed successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to print ledger", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to print ledger", "error");
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export ledger", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedTenant) {
                formDataToSend.append('tenant_id', selectedTenant);
            }
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APITenantLedgerExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export ledger", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedTenant) {
                formDataToSend.append('tenant_id', selectedTenant);
            }
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APITenantLedgerExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            tenant_id: '',
            entry_type: '',
            entry_verified: '',
            date_from: '',
            date_to: '',
            min_amount: '',
            max_amount: ''
        });
        setSearchTerm('');
        setSelectedTenant('');
        handleCloseOptions();
        fetchLedgerEntries();
    };

    const applyFilters = () => {
        handleCloseOptions();
        if (filters.tenant_id) {
            setSelectedTenant(filters.tenant_id);
            fetchLedgerEntries(filters.tenant_id);
        } else {
            fetchLedgerEntries();
        }
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);
    const handleCloseView = () => setOpenView(false);
    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    // Dialog handlers
    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add ledger entries", "error");
            return;
        }
        if (!selectedTenant) {
            showToast("Validation Error", "Please select a tenant first", "warning");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            tenant_id: selectedTenant,
            entry_date: new Date().toISOString().split('T')[0],
            entry_type: 'monthly_rent',
            debit: '',
            credit: '',
            notes: '',
            entry_verified: false
        });
        setErrors({
            tenant_id: false,
            entry_date: false,
            entry_type: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit ledger entries", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            tenant_id: record.tenant_id || selectedTenant,
            entry_date: record.entry_date || '',
            entry_type: record.entry_type || 'monthly_rent',
            debit: record.debit || '',
            credit: record.credit || '',
            notes: record.notes || '',
            entry_verified: record.entry_verified || false
        });
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete ledger entries", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleOpenVerify = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to verify entries", "error");
            return;
        }
        if (record.entry_verified) {
            showToast("Info", "This entry is already verified", "info");
            return;
        }
        setSelectedEntry(record);
        setVerifyDialogOpen(true);
    };

    const handleOpenRecalculate = () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to recalculate ledger", "error");
            return;
        }
        setRecalculateDialogOpen(true);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        if (name === 'entry_type') {
            const selectedType = entryTypeOptions.find(t => t.id === value);
            if (selectedType) {
                if (selectedType.sign === 'debit') {
                    setFormData(prev => ({ ...prev, [name]: value, credit: '' }));
                } else if (selectedType.sign === 'credit') {
                    setFormData(prev => ({ ...prev, [name]: value, debit: '' }));
                } else {
                    setFormData(prev => ({ ...prev, [name]: value }));
                }
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTenantSelect = (tenantId) => {
        setSelectedTenant(tenantId);
        fetchLedgerEntries(tenantId);
        setFilters(prev => ({ ...prev, tenant_id: tenantId }));
    };

    const validateForm = () => {
        if (!formData.tenant_id) {
            showToast("Validation Error", "Tenant is required", "warning");
            return false;
        }
        if (!formData.entry_date) {
            showToast("Validation Error", "Entry date is required", "warning");
            return false;
        }
        if (!formData.entry_type) {
            showToast("Validation Error", "Entry type is required", "warning");
            return false;
        }
        if (formData.entry_type !== 'payment' && !formData.debit && !formData.credit) {
            showToast("Validation Error", "Amount is required", "warning");
            return false;
        }
        if (formData.debit && formData.credit) {
            showToast("Validation Error", "Cannot have both debit and credit", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add ledger entries", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update ledger entries", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete ledger entries", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('tenant_id', formData.tenant_id);
            formDataToSend.append('entry_date', formData.entry_date);
            formDataToSend.append('entry_type', formData.entry_type);
            formDataToSend.append('debit', formData.debit || 0);
            formDataToSend.append('credit', formData.credit || 0);
            formDataToSend.append('notes', formData.notes || '');
            formDataToSend.append('entry_verified', formData.entry_verified ? 1 : 0);
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APITenantLedgerEntryStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APITenantLedgerEntryUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APITenantLedgerEntryDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchLedgerEntries(selectedTenant);
                fetchTenantBalances();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const getEntryTypeIcon = (type) => {
        const option = entryTypeOptions.find(opt => opt.id === type);
        return option?.icon || <Receipt />;
    };

    const getEntryTypeColor = (type) => {
        const option = entryTypeOptions.find(opt => opt.id === type);
        return option?.color || 'default';
    };

    const getEntryTypeText = (type) => {
        const option = entryTypeOptions.find(opt => opt.id === type);
        return option ? option.name : type;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const toggleRowExpand = (entryId) => {
        setExpandedRows(prev => ({
            ...prev,
            [entryId]: !prev[entryId]
        }));
    };

    // Filtered data
    const filteredEntries = ledgerEntries.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.notes?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = !filters.entry_type || 
            record.entry_type === filters.entry_type;

        const matchesVerified = filters.entry_verified === '' || 
            (filters.entry_verified === 'verified' ? record.entry_verified : !record.entry_verified);

        let matchesDate = true;
        if (filters.date_from && record.entry_date) {
            matchesDate = matchesDate && record.entry_date >= filters.date_from;
        }
        if (filters.date_to && record.entry_date) {
            matchesDate = matchesDate && record.entry_date <= filters.date_to;
        }

        const amount = record.debit || record.credit || 0;
        const matchesMinAmount = !filters.min_amount || amount >= parseFloat(filters.min_amount);
        const matchesMaxAmount = !filters.max_amount || amount <= parseFloat(filters.max_amount);

        return matchesSearch && matchesType && matchesVerified && matchesDate && matchesMinAmount && matchesMaxAmount;
    });

    // Pagination
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Calculate totals
    const totalDebits = ledgerEntries.reduce((sum, entry) => sum + (entry.debit || 0), 0);
    const totalCredits = ledgerEntries.reduce((sum, entry) => sum + (entry.credit || 0), 0);
    const currentBalance = tenantBalance || (totalDebits - totalCredits);

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading && ledgerEntries.length === 0) {
        return <LoadingState message="Loading Ledger Data..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={() => fetchLedgerEntries(selectedTenant)} title="Unable to Load Ledger Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Outstanding</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                        {formatCurrency(statistics.total_outstanding || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.error.light, width: 56, height: 56 }}>
                                    <MoneyOff sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Debits</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {formatCurrency(statistics.total_debits || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <TrendingUp sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Credits</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {formatCurrency(statistics.total_credits || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <TrendingDown sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Verified Entries</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {statistics.verified_entries || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <VerifiedUser sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Tenant Selection and Actions */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Tenant Ledger
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel>Select Tenant</InputLabel>
                        <Select
                            value={selectedTenant || ''}
                            onChange={(e) => handleTenantSelect(e.target.value)}
                            label="Select Tenant"
                        >
                            <MenuItem value="">All Tenants</MenuItem>
                            {tenants.map(tenant => (
                                <MenuItem key={tenant.id} value={tenant.id}>
                                    {tenant.full_name} - {tenant.phone_number}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && selectedTenant && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Add Entry
                            </Button>
                        )}
                        {hasPermission('EDIT') && selectedTenant && (
                            <Button
                                variant="contained"
                                color="info"
                                startIcon={<Calculate />}
                                onClick={handleOpenRecalculate}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Recalculate
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<GetApp />}
                                    onClick={handleExportClick}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                    Export
                                </Button>
                                {selectedTenant && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<Print />}
                                        onClick={handlePrintLedger}
                                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                                    >
                                        Print Ledger
                                    </Button>
                                )}
                            </>
                        )}
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchLedgerEntries(selectedTenant);
                                fetchTenantBalances();
                                fetchStatistics();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search entries..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            {/* Balance Summary Card */}
            {selectedTenant && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: "#2e7d32" }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle2" color="white">Total Debits (Amount Owed)</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>{formatCurrency(totalDebits)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle2" color="white">Total Credits (Amount Paid)</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>{formatCurrency(totalCredits)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle2" color="white">Current Balance</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: currentBalance > 0 ? '#ffeb3b' : '#a5d6a7' }}>
                                    {formatCurrency(currentBalance)}
                                </Typography>
                                {currentBalance > 0 && <Typography variant="caption" color="white">(Outstanding Balance)</Typography>}
                                {currentBalance < 0 && <Typography variant="caption" color="white">(Credit Balance)</Typography>}
                                {currentBalance === 0 && <Typography variant="caption" color="white">(Settled)</Typography>}
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            {/* Ledger Entries Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell width={50}>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Entry Type</TableCell>
                                    <TableCell align="right">Debit (UGX)</TableCell>
                                    <TableCell align="right">Credit (UGX)</TableCell>
                                    <TableCell align="right">Running Balance</TableCell>
                                    <TableCell>Verified</TableCell>
                                    <TableCell>Notes</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                    <React.Fragment key={record.id}>
                                        <TableRow hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>{record.entry_date}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getEntryTypeIcon(record.entry_type)}
                                                    label={getEntryTypeText(record.entry_type)}
                                                    color={getEntryTypeColor(record.entry_type)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {record.debit > 0 && (
                                                    <Typography sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
                                                        {formatCurrency(record.debit)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {record.credit > 0 && (
                                                    <Typography sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>
                                                        {formatCurrency(record.credit)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography sx={{ fontWeight: 'bold', color: record.running_balance > 0 ? theme.palette.error.main : record.running_balance < 0 ? theme.palette.success.main : theme.palette.text.primary }}>
                                                    {formatCurrency(record.running_balance)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {record.entry_verified ? (
                                                    <Chip icon={<VerifiedUser />} label="Verified" color="success" size="small" />
                                                ) : (
                                                    <Chip icon={<Pending />} label="Unverified" color="warning" size="small" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {record.notes ? (
                                                    <Tooltip title={record.notes}>
                                                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {record.notes}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {hasPermission('VIEW') && (
                                                    <Tooltip title="View Details"><IconButton onClick={() => handleOpenView(record)} color="primary"><ViewIcon /></IconButton></Tooltip>
                                                )}
                                                {hasPermission('EDIT') && !record.entry_verified && (
                                                    <>
                                                        <Tooltip title="Edit"><IconButton onClick={() => handleOpenEdit(record)} color="secondary"><EditIcon /></IconButton></Tooltip>
                                                        <Tooltip title="Verify"><IconButton onClick={() => handleOpenVerify(record)} color="success"><VerifiedUser /></IconButton></Tooltip>
                                                    </>
                                                )}
                                                {hasPermission('DELETE') && !record.entry_verified && (
                                                    <Tooltip title="Delete"><IconButton onClick={() => handleOpenDelete(record)} color="error"><Delete /></IconButton></Tooltip>
                                                )}
                                                {record.notes && record.notes.length > 50 && (
                                                    <Tooltip title="Expand Notes">
                                                        <IconButton onClick={() => toggleRowExpand(record.id)} color="info">
                                                            {expandedRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {expandedRows[record.id] && record.notes && (
                                            <TableRow>
                                                <TableCell colSpan={9} sx={{ bgcolor: theme.palette.grey[50], p: 2 }}>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {record.notes}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={filteredEntries.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ mt: 2 }} />
                    {filteredEntries.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <AccountBalance sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No ledger entries found</Typography>
                            <Typography variant="body2">Select a tenant or add an entry to get started</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Dialog */}
            <Dialog open={filterOptions} TransitionComponent={Transition} fullWidth maxWidth="sm" onClose={handleCloseOptions}>
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters}>Show All Entries</Button>
                        <Divider>OR</Divider>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>Filter Ledger Entries</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Entry Type</InputLabel>
                                    <Select name="entry_type" value={filters.entry_type} onChange={handleFilterChange} label="Entry Type">
                                        <MenuItem value="">All Types</MenuItem>
                                        {entryTypeOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Verification Status</InputLabel>
                                    <Select name="entry_verified" value={filters.entry_verified} onChange={handleFilterChange} label="Verification Status">
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="verified">Verified</MenuItem>
                                        <MenuItem value="unverified">Unverified</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date From" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date To" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Min Amount" name="min_amount" type="number" value={filters.min_amount} onChange={handleFilterChange} placeholder="Min amount" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Max Amount" name="max_amount" type="number" value={filters.max_amount} onChange={handleFilterChange} placeholder="Max amount" />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Entry Dialog */}
            <Dialog open={openAdd} onClose={handleCloseAdd} TransitionComponent={Transition} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "Add Ledger Entry" : "Edit Ledger Entry"}</b></DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>Entry Information</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Entry Date *" name="entry_date" type="date" value={formData.entry_date} onChange={handleInputChange} required InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Entry Type *</InputLabel>
                                    <Select name="entry_type" value={formData.entry_type} onChange={handleInputChange} label="Entry Type *">
                                        {entryTypeOptions.map(item => (
                                            <MenuItem key={item.id} value={item.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{item.icon} {item.name}</Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {(formData.entry_type === 'payment' || (formData.entry_type !== 'payment' && formData.debit === '' && formData.credit === '')) && (
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Amount *" name={formData.entry_type === 'payment' ? 'credit' : 'debit'} type="number" value={formData.entry_type === 'payment' ? formData.credit : formData.debit} onChange={handleInputChange} required InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                                </Grid>
                            )}
                            {formData.entry_type !== 'payment' && formData.entry_type !== 'opening_balance' && (
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleInputChange} multiline rows={3} placeholder="Reason for this entry..." />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <FormControlLabel control={<Switch name="entry_verified" checked={formData.entry_verified} onChange={handleInputChange} color="primary" />} label="Mark as Verified" />
                            </Grid>
                        </Grid>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">Cancel</Button>
                            <Button type="submit" color="primary" variant="contained">{postStatus === "ADD" ? "Add Entry" : "Update Entry"}</Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={openView} onClose={handleCloseView} TransitionComponent={Transition} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textAlign: 'center' }}><b>Ledger Entry Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}><Typography variant="h6" color="primary">Entry Information</Typography></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Entry ID:</b> {selectedRecord.ledger_entry_id}</Typography></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Date:</b> {selectedRecord.entry_date}</Typography></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Type:</b> <Chip icon={getEntryTypeIcon(selectedRecord.entry_type)} label={getEntryTypeText(selectedRecord.entry_type)} color={getEntryTypeColor(selectedRecord.entry_type)} size="small" /></Typography></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Verified:</b> {selectedRecord.entry_verified ? <Chip icon={<VerifiedUser />} label="Verified" color="success" size="small" /> : <Chip icon={<Pending />} label="Unverified" color="warning" size="small" />}</Typography></Grid>
                                {selectedRecord.debit > 0 && <Grid item xs={12}><Typography variant="subtitle2"><b>Debit Amount:</b> {formatCurrency(selectedRecord.debit)}</Typography></Grid>}
                                {selectedRecord.credit > 0 && <Grid item xs={12}><Typography variant="subtitle2"><b>Credit Amount:</b> {formatCurrency(selectedRecord.credit)}</Typography></Grid>}
                                <Grid item xs={12}><Typography variant="subtitle2"><b>Running Balance:</b> {formatCurrency(selectedRecord.running_balance)}</Typography></Grid>
                                {selectedRecord.notes && <Grid item xs={12}><Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography></Grid>}
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>System Information</Typography></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Created:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography></Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions><Button color="error" onClick={handleCloseView} variant="contained">Close</Button></DialogActions>
            </Dialog>

            {/* Recalculate Dialog */}
            <Dialog open={recalculateDialogOpen} onClose={() => setRecalculateDialogOpen(false)} TransitionComponent={Transition}>
                <DialogTitle sx={{ textAlign: 'center' }}>Recalculate Ledger</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>This will recalculate all running balances for this tenant's ledger. This action cannot be undone.</Alert>
                    <Typography variant="body2">Are you sure you want to proceed?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setRecalculateDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleRecalculate} color="warning" variant="contained">Recalculate</Button>
                </DialogActions>
            </Dialog>

            {/* Verify Dialog */}
            <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} TransitionComponent={Transition}>
                <DialogTitle sx={{ textAlign: 'center' }}>Verify Ledger Entry</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>Marking this entry as verified confirms its accuracy. This action cannot be undone.</Alert>
                    <Typography variant="body2">Entry: <strong>{getEntryTypeText(selectedEntry?.entry_type)}</strong> on <strong>{selectedEntry?.entry_date}</strong> for <strong>{formatCurrency(selectedEntry?.debit || selectedEntry?.credit)}</strong></Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setVerifyDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleVerifyEntry} color="success" variant="contained">Verify Entry</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDelete} onClose={handleCloseDelete} TransitionComponent={Transition}>
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Ledger Entry</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>Are you sure you want to delete this ledger entry?</Typography>
                    {selectedRecord && <Typography variant="h6" sx={{ textAlign: 'center' }}>{getEntryTypeText(selectedRecord.entry_type)} - {formatCurrency(selectedRecord.debit || selectedRecord.credit)}</Typography>}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>Warning: This will affect the running balance!</Typography>
                </DialogContent>
                <DialogActions><Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button><Button onClick={handleSubmit} color="primary" variant="contained">Confirm Delete</Button></DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default TenantLedger;

